/**
 * Page: LoginPage
 * Pacific theme v2 — white card, Karla font, brand gold CTA
 * Updated: 2026-03-31
 */
import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { auth, mfa, setToken, setRefreshToken, refreshTokenWithRefreshToken, resumeStoredSession } from '@/lib/api'
import { isNativePlatform, isAndroid, isBiometricEnabled, unlockWithBiometric } from '@/lib/native'
import { Browser } from '@capacitor/browser'
import { App } from '@capacitor/app'
import { nativeAppleSignIn } from '@/lib/native-apple-signin'
import { runNativeOAuth, parseOAuthCallback } from '@/lib/native-oauth-session'
import { beginPKCE } from '@/lib/oauth-pkce'
import { isOAuthCallbackURL, consumeOAuthCallbackURL, exchangeOAuthCode } from '@/lib/oauth-applink'
import { Input } from '@/components/atoms/Input'
import { useToast } from '@/components/molecules/Toast'
import { MortiseWordmark } from '@/components/atoms/MortiseMark'

// Keys MUST match the codes google_oauth.go actually emits, or real failures
// fall through to the generic string (they did — this map was keyed to codes
// the backend never sends). Shared between the web `?error=` redirect path
// and the native ASWebAuthenticationSession callback path (reach sweep,
// 2026-07-23 SSO audit followup) so a fix to one doesn't drift from the other.
const GOOGLE_SSO_ERROR_MESSAGES: Record<string, string> = {
  oauth_cancelled:     'You declined access to Google. No problem — try a different sign-in method.',
  oauth_csrf_failed:   'Google sign-in was interrupted or timed out. Please try again.',
  oauth_failed:        'Google sign-in failed. Please try again.',
  token_invalid:       'Google returned an unexpected response. Please try again.',
  token_decode_failed: 'Google returned an unexpected response. Please try again.',
  token_error:         'Google sign-in failed at the token step. Please try again.',
  no_email:            'That Google account doesn\'t have an email.',
  email_not_verified:  'That Google email isn\'t verified. Verify it in Google first, then retry.',
  sso_not_configured:  'Google sign-in isn\'t configured on this server yet.',
  token_issue_failed:  'Sign-in succeeded but the session couldn\'t be issued. Please try again.',
  // AUTH-02c Part 2 (#1520): the legacy raw-token redirect is retired; a build
  // too old to use the PKCE code flow now gets this instead of a raw session.
  upgrade_required:    'Please update the Tenon app to the latest version to sign in.',
}
// Pacific theme tokens — CSS variables for proper light/dark support
const P = {
  pageBg:     'var(--bg-page)',
  card:       'var(--bg-surface)',
  border:     'var(--border)',
  text:       'var(--text-primary)',
  textMuted:  'var(--text-secondary)',
  textDim:    'var(--text-muted)',
  inputBg:    'var(--bg-elevated)',
  inputBorder:'var(--border)',
  gold:       'var(--brand-gold)',
  goldHover:  'var(--brand-gold)',
  goldText:   'var(--bg-page)',
  font:       "'Karla', -apple-system, sans-serif",
}

export default function LoginPage() {
  const { error: toastError } = useToast()

  useEffect(() => {
    document.title = 'Tenon — Log in'
    return () => { document.title = 'Tenon' }
  }, [])

  useEffect(() => {
    if (sessionStorage.getItem('session_expired')) {
      sessionStorage.removeItem('session_expired')
      toastError('Your session expired. Please log in again.')
    }

    // Surface any SSO error code the backend redirected us back with.
    const params = new URLSearchParams(window.location.search)
    // Backend redirects failures with `?error=<code>` (google_oauth.go). Read
    // `error` first; keep `sso_error` as a fallback for any other SSO path.
    const ssoErr = params.get('error') || params.get('sso_error')
    if (ssoErr) {
      const messages: Record<string, string> = {
        ...GOOGLE_SSO_ERROR_MESSAGES,
        no_account: `No account found for ${params.get('sso_email') || 'that email'}. Sign up to get started.`,
      }
      toastError(messages[ssoErr] || `Google sign-in failed (${ssoErr}).`)
      // Strip the error params so a refresh doesn't re-trigger the toast.
      const cleanURL = new URL(window.location.href)
      cleanURL.searchParams.delete('error')
      cleanURL.searchParams.delete('sso_error')
      cleanURL.searchParams.delete('sso_email')
      window.history.replaceState({}, '', cleanURL.toString())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]       = useState<string | null>(null)
  const [loading, setLoading]   = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null)
  const [mfaCode, setMfaCode]   = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfaRemember, setMfaRemember] = useState(false)
  const [btnHover, setBtnHover] = useState(false)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  // App Store: SSO succeeded but no Tenon account exists. Show a form-less
  // "existing customers only" notice (no password, no signup, no link/price).
  const [noAccountNotice, setNoAccountNotice] = useState(false)
  // SIWA diagnostic banner removed 2026-06-04 — root cause + fix shipped in
  // PR #251 (simplified plugin) + PR #254 (backend dual-audience). Build 41+
  // confirmed working. The appendDiag() shim below stays as a no-op so the
  // call sites in handleAppleLogin compile without an audit pass; remove all
  // call sites the next time LoginPage is edited.
  const appendDiag = (_line: string) => { /* no-op */ }
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Surface the form-less "existing customers only" notice when we land here
  // via ?sso_no_account=1 — set by SignupPage's native guard and App.jsx's
  // deep-link router when Sign in with Apple / Google succeeds but no Tenon
  // account exists. Keeps the password signup form unreachable inside the app
  // (App Store Guideline 4 + 3.1.1).
  useEffect(() => {
    if (searchParams.get('sso_no_account') === '1') setNoAccountNotice(true)
  }, [searchParams])

  // Clicking "Sign in" with a live persisted device session should bypass the
  // form. Validate the access token and silently rotate the refresh token when
  // necessary so an expired 15-minute JWT does not cause a login-page bounce.
  useEffect(() => {
    if (searchParams.get('sso_no_account') === '1') return
    let active = true
    void resumeStoredSession().then(resumed => {
      if (active && resumed) navigate(searchParams.get('next') || '/projects', { replace: true })
    })
    return () => { active = false }
  }, [navigate, searchParams])

  // On a native platform with biometric unlock previously enabled, surface a
  // "Sign in with Face ID / Touch ID" button so the user doesn't have to type
  // their password again. The button calls unlockWithBiometric(); on success
  // we set the stored token and navigate.
  useEffect(() => {
    if (!isNativePlatform()) return
    isBiometricEnabled().then(setBiometricAvailable)
  }, [])

  // In the Electron desktop app, the OAuth flow opens in the system browser and
  // redirects back to tenon://auth-callback?token=...  The main process receives
  // the protocol URL and forwards it here via electronAPI.onAuthToken.
  useEffect(() => {
    const api = (window as any).electronAPI
    if (!api?.onAuthToken) return
    const cleanups: Array<() => void> = []
    cleanups.push(api.onAuthToken((token: string, next: string) => {
      void setToken(token)
      navigate(next || '/projects', { replace: true })
    }))
    // Desktop OAuth error surface (tenon://auth-callback?error=...).
    // Without this, Apple sign-in failures fall through to a silent stay on
    // the login screen — same code map as the iOS branch below.
    if (api.onAuthError) {
      cleanups.push(api.onAuthError((errCode: string) => {
        if (errCode === 'oauth_cancelled') return
        const desktopErrMessages: Record<string, string> = {
          oauth_config:        'Apple sign-in is not configured. Contact support.',
          oauth_failed:        'Apple sign-in failed. Try again.',
          token_invalid:       'Apple returned an unexpected response. Try again.',
          token_decode_failed: 'Apple returned an unexpected response. Try again.',
          no_sub:              'Apple sign-in failed — no user identifier. Try again.',
          token_error:         "Sign-in succeeded but your session couldn't be issued. Try again.",
          // AUTH-02d (#2362): a desktop build too old to send a PKCE challenge no
          // longer receives raw tokens over the tenon:// scheme — it's told to
          // update. Applies to both Google and Apple sign-in on desktop.
          update_required:     'Please update the Tenon desktop app to sign in.',
        }
        toastError(desktopErrMessages[errCode] || `Sign-in failed (${errCode}). Try again.`)
      }))
    }
    return () => { cleanups.forEach(c => c()) }
  }, [navigate, toastError])

  // On iOS/Android: after Google or Apple OAuth the backend redirects to
  // com.usetenon.app://auth?token=X&next=Y  — iOS intercepts the custom
  // scheme, closes the in-app browser, and fires appUrlOpen. Read the token
  // here and complete the login without any additional round-trip.
  //
  // 2026-05-21 — Apple review 2.1(a) on iPad iPadOS 26.4 said "app stopped
  // loading when we signed in via Sign in with Apple." Root cause: on iPad
  // iPadOS 26.4 the appUrlOpen event may not fire reliably when iOS
  // intercepts com.usetenon.app://, leaving SFSafariViewController stuck.
  // Defense-in-depth: also listen to appStateChange + force Browser.close
  // whenever the app becomes active during a pending OAuth. SceneDelegate
  // also force-dismisses presented controllers on the native side.
  useEffect(() => {
    if (!isNativePlatform()) return
    const handles: Array<{ remove: () => void }> = []
    // #2172 (AUTH-02c regression): the exchange itself — recognizing the
    // custom-scheme deep link vs the Android HTTPS App Link, trading a code
    // for tokens via PKCE, the single-use-code dedup — lives in
    // oauth-applink.ts, shared with the always-mounted app-root listener in
    // App.jsx (which needs the exact same logic so a cold-started callback
    // isn't silently dropped when this page isn't mounted). This function
    // only maps the shared result to LoginPage's own UI side effects.
    const consume = async (url: string): Promise<boolean> => {
      if (!isOAuthCallbackURL(url)) return false
      const result = await consumeOAuthCallbackURL(url)
      if (result.kind === 'ignored') return false
      if (result.kind === 'already-exchanged') return true

      sessionStorage.removeItem('ml_oauth_pending')
      Browser.close().catch(() => {})

      if (result.kind === 'no-account') {
        // com.usetenon.app://no-account?...  AND  com.usetenon.app://signup?...
        // Both mean "SSO succeeded but no Tenon account exists." App Store
        // Guideline 4 forbids a password step after Sign in with Apple, and
        // 3.1.1 + Joe's directive forbid any account-creation surface on
        // mobile. So BOTH deep-links resolve to the same form-less "existing
        // customers only" notice — no password, no form, no tappable signup
        // link, no price. Account creation is web-only.
        setNoAccountNotice(true)
      } else if (result.kind === 'auth-success') {
        navigate(result.next, { replace: true })
      } else if (result.kind === 'auth-error') {
        // Show an explanatory toast for actual failures; stay silent for
        // intentional cancellations (oauth_cancelled) — user knows they
        // tapped back.
        if (result.errCode !== 'oauth_cancelled') {
          const appleErrMessages: Record<string, string> = {
            oauth_config:        'Apple sign-in is not configured. Contact support.',
            oauth_failed:        'Apple sign-in failed. Try again.',
            token_invalid:       'Apple returned an unexpected response. Try again.',
            token_decode_failed: 'Apple returned an unexpected response. Try again.',
            no_sub:              'Apple sign-in failed — no user identifier. Try again.',
            token_error:         'Sign-in succeeded but your session couldn\'t be issued. Try again.',
            verifier_missing:    'Sign-in could not be completed. Please try again.',
            exchange_failed:     'Sign-in could not be completed. Please try again.',
            // AUTH-02c Part 2 (#1520): legacy raw-token redirect retired — a
            // build too old for the PKCE code flow gets this instead.
            upgrade_required:    'Please update the Tenon app to the latest version to sign in.',
          }
          toastError(appleErrMessages[result.errCode] || `Sign-in failed (${result.errCode}). Try again.`)
        }
      }
      return true
    }
    App.addListener('appUrlOpen', ({ url }) => { consume(url) }).then(h => handles.push(h))
    // Backstop: when the app foregrounds during a pending OAuth, force-close
    // the browser. On iPad iPadOS 26.4 this catches the case where iOS
    // delivered the URL but Capacitor didn't surface appUrlOpen reliably.
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return
      if (sessionStorage.getItem('ml_oauth_pending') !== '1') return
      // Give appUrlOpen one tick to fire first. If it did, ml_token is now
      // set + we navigate. If not, just close the browser so the user is
      // unstuck — they can tap "Continue with Apple" again.
      setTimeout(() => {
        if (sessionStorage.getItem('ml_oauth_pending') === '1') {
          Browser.close().catch(() => {})
        }
      }, 300)
    }).then(h => handles.push(h))
    return () => { handles.forEach(h => h.remove()) }
  }, [navigate])

  const handleBiometricUnlock = async () => {
    const refreshToken = await unlockWithBiometric('Sign in to Tenon')
    if (!refreshToken) {
      toastError('Biometric sign-in cancelled.')
      return
    }
    const success = await refreshTokenWithRefreshToken(refreshToken)
    if (!success) {
      toastError('Biometric sign-in failed. Please sign in manually.')
      return
    }
    navigate(searchParams.get('next') || '/projects', { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await auth.login(email, password) as any
      if (!res) throw new Error('Login failed. Please try again.')
      if (res.mfa_required) {
        setMfaChallenge(res.challenge_token)
      } else {
        await setToken(res.token)
        navigate(searchParams.get('next') || '/projects', { replace: true })
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaChallenge) return
    setError(null)
    setMfaLoading(true)
    try {
      const res = await mfa.completeLogin(mfaChallenge, mfaCode, mfaRemember)
      if (res.trusted_device_token) {
        localStorage.setItem('tenon_trusted_device', res.trusted_device_token)
      }
      await setToken(res.token)
      navigate(searchParams.get('next') || '/projects', { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid code')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    const next = searchParams.get('next') || '/projects'

    // Native iOS / Capacitor — use ASWebAuthenticationSession via the
    // OAuthSession plugin so the custom-scheme callback comes back to the
    // app cleanly. SFSafariViewController + custom-scheme redirect is broken
    // on iOS 17+: tap → server redirects to `com.usetenon.app://auth?token=…`
    // → Safari shows a 0-byte "google" data file with an Open-in dialog
    // instead of bouncing the URL back. ASWebAuthenticationSession captures
    // the callback URL natively. (See OAuthSessionPlugin.swift.)
    if (isNativePlatform()) {
      // AUTH-02 (#728): mint + stash a PKCE verifier and send only its S256
      // challenge into the flow, so the callback can carry a code instead of
      // raw tokens.
      // AUTH-02c (#1520): Android builds with the verified App Link filter
      // declare `applink=1` so the callback comes back on the HTTPS App Link
      // (delivered only to our signed package) instead of the hijackable
      // custom scheme. iOS keeps ASWebAuthenticationSession + custom scheme.
      const authURL =
        'https://app.usetenon.com/api/v1/auth/google?next=' +
        encodeURIComponent(next) + '&native=1' + (await beginPKCE()) +
        (isAndroid() ? '&applink=1' : '')
      try {
        const res = await runNativeOAuth(authURL, 'com.usetenon.app')
        if (!res) {
          // Plugin missing — fall through to the legacy Browser.open() path
          // so a stale build without the plugin still works.
          sessionStorage.setItem('ml_oauth_pending', '1')
          Browser.open({ url: authURL, presentationStyle: 'fullscreen' })
          return
        }
        const parsed = parseOAuthCallback(res.callbackURL)
        if (parsed.error) {
          toastError(GOOGLE_SSO_ERROR_MESSAGES[parsed.error] || `Google sign-in failed (${parsed.error}).`)
          return
        }
        // AUTH-02 (#728): preferred path — exchange the single-use code for
        // tokens, proving possession of the PKCE verifier. Goes through the
        // same exchangeOAuthCode() as the appUrlOpen consume() path (shared
        // dedup guard): on iPad the appUrlOpen listener can also fire for a
        // callback ASWebAuthenticationSession already returned here, and the
        // loser would spend a dead code and toast an error over a successful
        // login.
        if (parsed.code) {
          const result = await exchangeOAuthCode(parsed.code, parsed.next || next)
          if (result.kind === 'auth-success') {
            navigate(result.next, { replace: true })
          } else if (result.kind === 'auth-error') {
            toastError('Google sign-in could not be completed. Try again.')
          }
          return
        }
        // AUTH-02c Part 2 (#1520): the legacy token-in-URL path is retired —
        // parseOAuthCallback no longer returns raw tokens and the server no
        // longer emits them. A pre-code-flow build arrives here with neither a
        // code nor a token; it surfaces as the "returned no token" toast below,
        // and the server has already sent `?error=upgrade_required` on its own
        // redirect, handled by parsed.error above.
        // No Tenon account for that Google email. Backend now sends
        // `com.usetenon.app://no-account?...` on native (App Store: no mobile
        // signup). Show the form-less "existing customers only" notice. The
        // legacy `://signup` host is handled the same way on native.
        const url = new URL(res.callbackURL)
        if (url.host === 'no-account' || url.host === 'signup' || url.pathname.startsWith('/signup')) {
          setNoAccountNotice(true)
          return
        }
        toastError('Google sign-in returned no token')
      } catch (err: any) {
        if (err?.message === 'cancelled') return
        toastError(`Google sign-in failed: ${err?.message || 'unknown'}`)
      }
      return
    }

    if ((window as any).electronAPI?.openOAuth) {
      ;(window as any).electronAPI.openOAuth('google', next)
      return
    }

    window.location.href = '/api/v1/auth/google?next=' + encodeURIComponent(next)
  }

  const handleAppleLogin = async () => {
    const next = searchParams.get('next') || '/projects'
    // TEMP-SIWA-DIAG: report each step on-screen. Remove with siwaDiag state.
    appendDiag('tapped')
    const pluginRegistered = !!(window as any).Capacitor?.Plugins?.AppleSignIn
    appendDiag(`native plugin check: ${pluginRegistered ? 'registered' : 'NOT registered'}`)
    if (isNativePlatform() && pluginRegistered) {
      try {
        appendDiag('calling nativeAppleSignIn')
        const creds = await nativeAppleSignIn()
        appendDiag(`got credentials (idToken len=${creds.identityToken?.length || 0})`)
        appendDiag('posting to backend')
        const res = await auth.appleNative(creds.identityToken, creds.user)
        appendDiag(`backend: token=${!!res.token} redirect=${res.redirect || 'none'}`)
        if (res.token) {
          if (res.refresh_token) await setRefreshToken(res.refresh_token)
          await setToken(res.token)
          navigate(res.next || next)
          return
        }
        if (res.redirect === 'no_account' || res.redirect === 'signup') {
          // App Store: no account creation on mobile. Show the form-less
          // "existing customers only" notice (no password, per Guideline 4).
          setNoAccountNotice(true)
          return
        }
        // Defensive: the backend returned neither a token nor a known redirect.
        // NEVER leave the user on the login page with no result (Apple 2.1a
        // "no action took place"). Fall back to the form-less notice.
        appendDiag('backend: no token/redirect → showing no-account notice')
        setNoAccountNotice(true)
        return
      } catch (err: any) {
        const msg = err?.message || err?.errorMessage || String(err)
        appendDiag(`plugin error: ${msg.slice(0, 80)}`)
        if (msg === 'cancelled') return
        // Any other failure (network, token verify, backend error): show a
        // visible message rather than silently stranding the user on the login
        // page — that silent return is exactly the "no action" Apple flagged.
        setError('Could not complete Sign in with Apple. Please try again, or sign in with your email and password below.')
        return
      }
    } else if (isNativePlatform()) {
      // Fallback: SFSafariViewController path (web-OAuth on native, for devices
      // where the Capacitor plugin isn't registered — guards degraded states).
      // This is ALSO the live Apple sign-in path on Android, so it carries the
      // PKCE challenge (AUTH-02 #728) — the callback comes back via appUrlOpen.
      sessionStorage.setItem('ml_oauth_pending', '1')
      // AUTH-02c (#1520): see the applink note on the Google path above —
      // this Browser.open fallback is the live Apple sign-in path on Android.
      Browser.open({
        url: 'https://app.usetenon.com/api/v1/auth/apple?next=' + encodeURIComponent(next) +
          '&native=1' + (await beginPKCE()) + (isAndroid() ? '&applink=1' : ''),
        presentationStyle: 'fullscreen',
      })
    } else if ((window as any).electronAPI?.openOAuth) {
      ;(window as any).electronAPI.openOAuth('apple', next)
    } else {
      window.location.href = '/api/v1/auth/apple?next=' + encodeURIComponent(next)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: P.pageBg,
      fontFamily: P.font,
      paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: '16px',
      paddingRight: '16px',
      position: 'relative',
      overflowX: 'hidden',
    }}>
      {/* Gold radial gradient — subtle brand warmth in the top-right corner */}
      <div aria-hidden style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(80% 60% at 100% 0%, color-mix(in oklch, var(--brand-gold) 10%, transparent), transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      <div style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <a href="https://usetenon.com" style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '28px',
          textDecoration: 'none',
        }}>
          {/* No `light` prop: the login page follows the active theme, so the
              wordmark must too. Forcing the light palette rendered the "Tenon"
              text in the light-mode dark ink on the dark page — near-invisible
              (Joe, dark-mode contrast report 2026-06-22). */}
          <MortiseWordmark size={22} />
        </a>

        {/* Card */}
        <div style={{
          width: '100%',
          background: P.card,
          border: `1px solid ${P.border}`,
          borderRadius: '12px',
          padding: '36px 40px',
          boxShadow: 'var(--shadow-2)',
        }}>

          {mfaChallenge ? (
            /* ── MFA view ── */
            <form onSubmit={handleMFASubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: 10, marginBottom: 12,
                  background: 'color-mix(in oklch, var(--brand-gold) 15%, var(--bg-elevated))',
                  color: 'var(--brand-gold)',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                </div>
                <h2 style={{
                  margin: '0 0 6px',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  color: P.text,
                  letterSpacing: '-0.025em',
                }}>
                  Two-step verification
                </h2>
                <p style={{ margin: 0, fontSize: '0.875rem', color: P.textMuted }}>
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              {error && (
                <div style={{
                  padding: '10px 14px',
                  background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                  borderRadius: '8px',
                  color: 'var(--st-error)',
                  fontSize: '0.85rem',
                }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: P.text }}>
                  Authentication Code
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  required
                  autoFocus
                  style={{ letterSpacing: '0.3em', fontSize: '20px', textAlign: 'center' }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={mfaRemember}
                  onChange={e => setMfaRemember(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: P.gold, cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.85rem', color: P.textMuted }}>Remember this device for 30 days</span>
              </label>
              <button
                type="submit"
                disabled={mfaLoading || mfaCode.length < 6}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: (mfaLoading || mfaCode.length < 6) ? 'color-mix(in oklch, var(--brand-gold) 60%, transparent)' : P.gold,
                  color: P.goldText,
                  fontFamily: P.font,
                  fontSize: '1rem',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (mfaLoading || mfaCode.length < 6) ? 'not-allowed' : 'pointer',
                }}
              >
                {mfaLoading ? 'Verifying…' : 'Verify Code'}
              </button>
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setMfaChallenge(null); setMfaCode(''); setError(null) }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: P.textMuted,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    fontFamily: P.font,
                  }}
                >
                  ← Back to log in
                </button>
              </div>
            </form>
          ) : (
            /* ── Normal login view ── */
            <>
              <div style={{ marginBottom: 6 }}>
                <span style={{
                  display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
                  background: 'var(--brand-gold)', marginRight: 8, verticalAlign: 'middle',
                }} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                  Tenon · Sign in
                </span>
              </div>
              <h1 style={{
                fontSize: '1.625rem',
                fontWeight: 700,
                color: P.text,
                margin: '0 0 6px',
                letterSpacing: '-0.025em',
                lineHeight: 1.1,
              }}>
                Welcome back
              </h1>
              <p style={{
                fontSize: '14px',
                color: P.textMuted,
                margin: '0 0 28px',
                lineHeight: 1.55,
              }}>
                Log in to your Tenon account
              </p>

              {error && (
                <div style={{
                  padding: '10px 14px',
                  background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                  borderRadius: '8px',
                  color: 'var(--st-error)',
                  fontSize: '0.85rem',
                  marginBottom: '16px',
                }}>
                  {error}
                </div>
              )}

              {noAccountNotice ? (
                // Guideline 4: after Sign in with Apple with an Apple ID that has
                // no Tenon account, show a FORM-LESS message ONLY. No email,
                // password, or name field is shown, and no copy directs the user
                // to enter credentials — the entire sign-in form is hidden. This
                // is the whole screen the user sees after SIWA in the no-account
                // case, so nothing here may re-request name/email/password.
                <div>
                  <div style={{
                    padding: '16px 18px',
                    background: 'color-mix(in oklch, var(--brand-gold) 8%, transparent)',
                    border: '1px solid color-mix(in oklch, var(--brand-gold) 40%, transparent)',
                    borderRadius: '10px',
                    color: 'var(--text-primary)',
                    fontSize: '0.92rem',
                    lineHeight: 1.55,
                    marginBottom: '18px',
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '1rem' }}>
                      This Apple&nbsp;ID isn't linked to a Tenon account
                    </div>
                    Tenon accounts are created for your company during onboarding. Ask your
                    company administrator to add you, or visit usetenon.com on a computer to
                    get started. You're all set with Apple — nothing else to enter here.
                  </div>
                  <button
                    type="button"
                    onClick={() => { setNoAccountNotice(false); setError('') }}
                    style={{
                      width: '100%',
                      padding: '13px 16px',
                      background: 'var(--bg-elevated)',
                      border: `1px solid ${P.inputBorder}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: P.text,
                      fontFamily: P.font,
                    }}
                  >
                    Back
                  </button>
                </div>
              ) : (
                <>
              {/* Google OAuth */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '11px 16px',
                  background: 'var(--bg-elevated)',
                  border: `1px solid ${P.inputBorder}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: P.text,
                  fontFamily: P.font,
                  marginBottom: '10px',
                }}
                onMouseOver={e => (e.currentTarget.style.background = 'var(--bg-surface)')}
                onMouseOut={e => (e.currentTarget.style.background = 'var(--bg-elevated)')}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              {/* Apple Sign In */}
              <button
                type="button"
                onClick={handleAppleLogin}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '11px 16px',
                  // Apple HIG "Sign in with Apple" black button: solid black with
                  // white text + white logo. The dark-navy/dark-text combo read as
                  // an invisible button on the dark login card (Joe contrast report
                  // 2026-06-22). A subtle light border delineates the black button
                  // from the dark page; on the light theme the black button already
                  // has strong contrast and the border is barely visible.
                  background: '#000',
                  border: '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#fff',
                  fontFamily: P.font,
                  marginBottom: '20px',
                }}
                onMouseOver={e => (e.currentTarget.style.background = '#1a1a1a')}
                onMouseOut={e => (e.currentTarget.style.background = '#000')}
              >
                <svg width="16" height="20" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" fill="#fff" style={{ marginTop: '-2px' }}>
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                Continue with Apple
              </button>

              {/* SIWA diagnostic block removed — fix shipped in PR #251 + #254.
                  Build 41+ confirmed working. */}

              {/* Biometric unlock — native-only, only after user enabled it
                  via Profile → Mobile → Face ID / Touch ID toggle. */}
              {biometricAvailable && (
                <button
                  type="button"
                  onClick={handleBiometricUnlock}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '11px 16px',
                    background: P.gold,
                    border: `1px solid ${P.gold}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: P.goldText,
                    fontFamily: P.font,
                    marginBottom: '20px',
                  }}
                >
                  Sign in with biometrics
                </button>
              )}

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ flex: 1, height: '1px', background: P.inputBorder }} />
                <span style={{ fontSize: '0.78rem', color: P.textDim, fontWeight: 500 }}>or continue with email</span>
                <div style={{ flex: 1, height: '1px', background: P.inputBorder }} />
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  <label htmlFor="email" style={{ fontSize: '0.85rem', fontWeight: 600, color: P.text }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    id="email"
                    placeholder="you@yourcompany.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                  <label htmlFor="password" style={{ fontSize: '0.85rem', fontWeight: 600, color: P.text }}>
                    Password
                  </label>
                  <Input
                    type="password"
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ fontSize: '0.85rem', color: P.textMuted }}>Signed in on this device for 30 days</span>
                  <Link
                    to="/forgot-password"
                    style={{
                      fontSize: '0.82rem',
                      color: P.textMuted,
                      textDecoration: 'none',
                    }}
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: loading ? 'color-mix(in oklch, var(--brand-gold) 60%, transparent)' : (btnHover ? P.goldHover : P.gold),
                    color: P.goldText,
                    fontFamily: P.font,
                    fontSize: '1rem',
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s, transform 0.15s',
                    transform: (!loading && btnHover) ? 'translateY(-1px)' : 'none',
                    letterSpacing: '-0.01em',
                    marginBottom: '20px',
                  }}
                >
                  {loading ? 'Logging in…' : 'Log In'}
                </button>

                {/* App Store: no signup affordance on native iOS (no account
                    creation on mobile — Guideline 4 + 3.1.1). Web/desktop keep it. */}
                {!isNativePlatform() && (
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: P.textMuted }}>
                      Don't have an account?{' '}
                      <Link
                        to="/signup"
                        style={{
                          color: P.text,
                          fontWeight: 600,
                          textDecoration: 'none',
                          borderBottom: `2px solid ${P.gold}`,
                          paddingBottom: '1px',
                        }}
                      >Sign up</Link>
                    </span>
                  </div>
                )}
              </form>
                </>
              )}
            </>
          )}
        </div>

        {/* Sage callout */}
        <div style={{
          marginTop: '20px',
          background: 'color-mix(in oklch, var(--brand-gold) 7%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--brand-gold) 22%, var(--border-subtle))',
          borderRadius: '10px',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          width: '100%',
        }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '7px',
            flexShrink: 0,
            background: P.gold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '0.75rem',
            color: P.goldText,
            fontFamily: P.font,
            letterSpacing: '-0.01em',
          }}>
            S
          </div>
          <p style={{ fontSize: '0.82rem', color: P.textMuted, lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: P.text }}>Sage is watching your projects.</strong>{' '}
            Log in to see what needs your attention today.
          </p>
        </div>

        {/* Footer links */}
        <div style={{ marginTop: '24px', display: 'flex', gap: '20px' }}>
          {[['Terms', '/terms'], ['Privacy', '/privacy']].map(([label, path]) => (
            <Link key={label} to={path} style={{
              color: P.textDim,
              fontSize: '0.78rem',
              textDecoration: 'none',
            }}>
              {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
